import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { setDocumentTitle } from '../utils/titleManager';
import { Amplify } from 'aws-amplify';
import { post, put, get } from 'aws-amplify/api';
import { validateRequiredFields } from '../utils/eventValidation';
import { validateDate, validateGuestLimit, validateDescription, validateAdditionalInfo } from '../utils/validation';
import { sanitizeFilename, sanitizeText } from '../utils/inputSanitization';
import { validateCssFile, uploadCssFile } from '../utils/cssUploadUtils';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { showAlert } from '../utils/alerts';
import { createAutoSave } from '../utils/autoSave';
import { validateRsvpCutoff } from '../utils/rsvpUtils';

function EditEventScreen() {
  const [event, setEvent] = useState(null);
  const [name, setName] = useState('');
  const [heading, setHeading] = useState('');
  const [description, setDescription] = useState('');
  const [customCss, setCustomCss] = useState(null);
  const [customCssUrl, setCustomCssUrl] = useState('');
  const [customCssType, setCustomCssType] = useState('url'); // 'url' or 'file'
  const [date, setDate] = useState('');
  const [maxGuests, setMaxGuests] = useState('');
  const [location, setLocation] = useState('');
  const [rsvpCutoff, setRsvpCutoff] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [rsvpDetails, setRsvpDetails] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [recapText, setRecapText] = useState('');
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'
  const navigate = useNavigate();
  const { id } = useParams();
  
  const autoSaveRef = useRef(null);

  useEffect(() => {
    setDocumentTitle('Edit Event', event?.name);
  }, [event]);

  // Initialize auto-save
  useEffect(() => {
    const saveFunction = async (data) => {
      setSaveStatus('saving');
      try {
        const restOperation = put({
          apiName: 'events',
          path: `/events/${id}`,
          options: {
            body: data
          }
        });
        await restOperation.response;
        setSaveStatus('saved');
      } catch (err) {
        console.error('Auto-save failed:', err);
        setSaveStatus('error');
        // Don't throw the error, just log it and set the error status
        // This allows the auto-save to continue working even after an error
      }
    };

    autoSaveRef.current = createAutoSave(saveFunction);

    return () => {
      if (autoSaveRef.current) {
        autoSaveRef.current.destroy();
      }
    };
  }, [id]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    const updateState = {
      name: setName,
      heading: setHeading,
      description: setDescription,
      date: setDate,
      maxGuests: setMaxGuests,
      location: setLocation,
      rsvpCutoff: setRsvpCutoff,
      instructions: setInstructions,
      rsvpDetails: setRsvpDetails,
      additionalInfo: setAdditionalInfo,
      recapText: setRecapText,
    }[name];

    if (updateState) {
      updateState(value);
      
      // Prepare data for auto-save
      const formData = {
        name,
        heading,
        description,
        date,
        location,
        maxGuests: maxGuests ? parseInt(maxGuests, 10) : null,
        rsvpCutoff: rsvpCutoff || null,
        instructions,
        rsvpDetails,
        additionalInfo,
        recapText,
        customCssUrl: customCssType === 'url' ? customCssUrl : null
      };
      
      // Schedule auto-save
      if (autoSaveRef.current) {
        autoSaveRef.current.scheduleAutoSave(formData);
      }
    }
  }, [name, heading, description, date, maxGuests, location, instructions, rsvpDetails, additionalInfo, recapText, customCssType, customCssUrl]);

  const handleBlur = useCallback(() => {
    if (autoSaveRef.current) {
      autoSaveRef.current.handleBlur();
    }
  }, []);
  useEffect(() => {
    async function fetchEvent() {
      try {
        setLoading(true);
        const restOperation = get({
          apiName: 'events',
          path: `/events/${id}`,
        });
        const { body } = await restOperation.response;
        const { event } = await body.json();
        setEvent(event);
        setName(event.name || '');
        setDescription(event.description || '');
        setDate(event.date || '');
        if (event.customCssUrl) {
          setCustomCssUrl(event.customCssUrl);
          setCustomCssType('url');
        }
        setHeading(event.heading || '');
        setMaxGuests(event.maxGuests || event.guestCount || '');
        setLocation(event.location || '');
        setRsvpCutoff(event.rsvpCutoff || '');
        setAdditionalInfo(event.additionalInfo || '');
        setInstructions(event.instructions || '');
        setRsvpDetails(event.rsvpDetails || '');
        setRecapText(event.recapText || '');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchEvent();
  }, [id]);

  const validateFields = () => {
    if (!name || !date || !heading || !location || !instructions || !rsvpDetails) {
      throw new Error('Please fill in all required fields: Event Name, Date, Heading, Location, Instructions, and RSVP Details');
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      throw new Error('Date must be in YYYY-MM-DD format');
    }

    if (maxGuests && (!Number.isInteger(Number(maxGuests)) || Number(maxGuests) <= 0)) {
      throw new Error('Maximum guests must be a positive number');
    }

    // Validate RSVP cutoff if provided
    if (rsvpCutoff) {
      const cutoffValidation = validateRsvpCutoff(rsvpCutoff, date);
      if (!cutoffValidation.isValid) {
        throw new Error(cutoffValidation.error);
      }
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    try {
      setLoading(true);
      validateFields();
      
      const updateData = {
        name,
        heading,
        description,
        date,
        location,
        maxGuests: maxGuests ? parseInt(maxGuests, 10) : null,
        instructions,
        rsvpDetails: rsvpDetails,
        additionalInformation: additionalInfo,
        templateFormat: event?.templateFormat || 'default',
        customCssUrl: customCssType === 'url' ? customCssUrl : await handleCssUpload()
      };
      
      if (!name || !date || !heading || !location || !instructions || !rsvpDetails) {
        throw new Error('Please fill in all required fields: Event Name, Date, Heading, Location, Instructions, and RSVP Details');
      }

      // Validate date format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(date)) {
        throw new Error('Date must be in YYYY-MM-DD format');
      }

      // Validate maxGuests if provided
      if (maxGuests && (!Number.isInteger(Number(maxGuests)) || Number(maxGuests) <= 0)) {
        throw new Error('Maximum guests must be a positive number');
      }
      setError(null);
      // Ensure consistent date format
      if (!updateData.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        throw new Error('Date must be in YYYY-MM-DD format');
      }

      const restOperation = put({
        apiName: 'events',
        path: `/events/${id}`,
        options: {
          body: {
            name: sanitizeText(name),
            heading: sanitizeText(heading),
            description: sanitizeText(description),
            date,
            maxGuests: maxGuests ? parseInt(maxGuests, 10) : null,
            location: sanitizeText(location),
            rsvpCutoff: rsvpCutoff || null,
            instructions: sanitizeText(instructions),
            rsvpDetails: sanitizeText(rsvpDetails),
            additionalInformation: additionalInfo ? sanitizeText(additionalInfo) : '',
            recapText: recapText ? sanitizeText(recapText) : ''
          }
        }
      });
      await restOperation.response;
      navigate('/events')
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  const handleCssUpload = async () => {
    if (!customCss) return null;
    
    try {
      validateCssFile(customCss);
      const response = await uploadCssFile(customCss);
      return response.url;
    } catch (error) {
      throw new Error(`CSS Upload failed: ${error.message}`);
    }
  };

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-4">Edit Event</h1>
      {saveStatus === 'saving' && (
        <div className="text-blue-500 mb-2">Saving changes...</div>
      )}
      {saveStatus === 'error' && (
        <div className="text-red-500 mb-2">Failed to save changes. Your changes will be retried automatically.</div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block mb-1">Name:</label>
          <input
            type="text"
            name="name"
            value={name}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
            required
          />
        </div>
        <div>
          <label className="block mb-1">Heading:</label>
          <input
            type="text"
            name="heading"
            value={heading}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
          />
        </div>
        
        <div>
          <label className="block mb-1">Custom CSS:</label>
          <div className="mb-2">
            <select 
              value={customCssType}
              onChange={(e) => setCustomCssType(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="url">Enter URL</option>
              <option value="file">Upload File</option>
            </select>
          </div>
          
          {customCssType === 'url' ? (
            <input
              type="url"
              value={customCssUrl}
              onChange={(e) => {
                setCustomCssUrl(e.target.value);
                if (autoSaveRef.current) {
                  autoSaveRef.current.scheduleAutoSave({ ...formData, customCssUrl: e.target.value });
                }
              }}
              onBlur={handleBlur}
              placeholder="Enter CSS URL"
              className="w-full p-2 border rounded"
            />
          ) : (
            <input
              type="file"
              accept=".css"
              onChange={(e) => setCustomCss(e.target.files[0])}
              className="w-full p-2 border rounded"
            />
          )}
        </div>

        <div>
          <label className="block mb-1">Description:</label>
          <textarea
            name="description"
            value={description}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded mb-4"
            rows="4"
          />
        </div>
        <div>
          <label className="block mb-1">Date:</label>
          <input
            type="date"
            name="date"
            value={date}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
          />
        </div>
        <div>
          <label className="block mb-1">Max Guests:</label>
          <input
            type="number"
            name="maxGuests"
            value={maxGuests}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
          />
        </div>
        <div>
          <label className="block mb-1">RSVP Cutoff:</label>
          <input
            type="datetime-local"
            name="rsvpCutoff"
            value={rsvpCutoff}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
          />
        </div>
        <div>
          <label className="block mb-1">Location:</label>
          <input
            type="text"
            name="location"
            value={location}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
          />
        </div>
        <div>
          <label className="block mb-1">Instructions:</label>
          <textarea
            name="instructions"
            value={instructions}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
            rows="4"
          />
        </div>
        <div>
          <label className="block mb-1">RSVP Details:</label>
          <textarea
            name="rsvpDetails"
            value={rsvpDetails}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
            rows="4"
          />
        </div>
        <div>
          <label className="block mb-1">Additional Information:</label>
          <textarea
            name="additionalInfo"
            value={additionalInfo}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
            rows="4"
          />
        </div>
        <div>
          <label className="block mb-1">Recap Text:</label>
          <textarea
            name="recapText"
            value={recapText}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-full p-2 border rounded"
            rows="4"
            placeholder="Enter text for the recap page (supports Markdown)"
          />
          <small className="text-gray-600">This text will be displayed on the recap page. Supports Markdown formatting (e.g., **bold**, *italic*, [links](url)).</small>
        </div>
        <button
          type="submit"
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          disabled={loading || saveStatus === 'saving'}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
}

export default EditEventScreen;













